import z from 'zod';

/** `reason` as the service validates it (`CreateServiceProviderReportRequest`). */
export const PROVIDER_REPORT_REASONS = [
  'spam',
  'fraud_or_scam',
  'inappropriate_content',
  'misleading_information',
  'poor_service_quality',
  'unprofessional_behavior',
  'other',
] as const;

export type ProviderReportReason = (typeof PROVIDER_REPORT_REASONS)[number];

export const PROVIDER_REPORT_STATUSES = ['new', 'in_review', 'resolved', 'dismissed'] as const;

export type ProviderReportStatus = (typeof PROVIDER_REPORT_STATUSES)[number];

type Page<T> = {
  items: T[];
  count?: number;
};

/** `POST /:id/report` on the service-provider service. One open report per user and provider. */
export type CreateProviderReportDto = {
  reason: ProviderReportReason;
  details?: string;
};

/** The reporter's own view — `GET /reports` and the `POST` response. */
export type SubmittedProviderReportDto = {
  id: string;
  service_provider_id: string;
  service_provider_name: string;
  reason: ProviderReportReason;
  details: string | null;
  status: ProviderReportStatus;
  created_at: string;
};

/** The Platform Admin view — `GET /admin/reports`, with who filed it. */
export type AdminProviderReportDto = SubmittedProviderReportDto & {
  reporter_id: string;
  service_id: string;
  updated_at: string;
};

export type SubmittedProviderReportsPageDto = Page<SubmittedProviderReportDto>;
export type AdminProviderReportsPageDto = Page<AdminProviderReportDto>;

/** `PATCH /admin/reports/:report_id/status`. */
export type UpdateProviderReportStatusDto = {
  status: ProviderReportStatus;
};

export const providerReportFormSchema = z
  .object({
    reason: z.enum(PROVIDER_REPORT_REASONS, { message: 'Please choose a reason.' }),
    details: z.string().trim().max(2000, 'Please keep the details under 2000 characters.'),
  })
  .refine(({ reason, details }) => reason !== 'other' || details.length > 0, {
    path: ['details'],
    message: 'Please tell us what’s wrong.',
  });

export type ProviderReportFormValues = z.infer<typeof providerReportFormSchema>;
